import React, { useEffect, useMemo, useState } from 'react';
import './ThicknessPage.css';
import Lens3DPreview from './Lens3DPreview';
import { FRAME_SHAPES } from './frameShapes';
import {
  computeThicknessStudy,
  createDefaultThicknessForm,
  DEFAULT_INDICES,
  DEFAULT_DESIGNS,
} from './thicknessEngine';
import { updateArchive } from './archiveManager';

export default function ThicknessPage({ goTo, data }) {
  const archive = data ?? {};
  const [form, setForm] = useState(() => ({
    ...createDefaultThicknessForm(archive),
    ...(archive.thicknessStudy?.input ?? {}),
  }));
  const [saveState, setSaveState] = useState('idle');
const [viewMode, setViewMode] = useState('2d');
const [selected3DMaterial, setSelected3DMaterial] = useState('');

const study = useMemo(() => computeThicknessStudy(form), [form]);

const available3DMaterials = useMemo(() => {
  const rightLabels = (study?.right?.comparisons ?? []).map((item) => item.label);
  const leftLabels = (study?.left?.comparisons ?? []).map((item) => item.label);

  return Array.from(new Set([...rightLabels, ...leftLabels]));
}, [study]);

useEffect(() => {
  if (!available3DMaterials.length) {
    setSelected3DMaterial('');
    return;
  }

  setSelected3DMaterial((prev) =>
    available3DMaterials.includes(prev) ? prev : available3DMaterials[0]
  );
}, [available3DMaterials]);

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleRxChange = (eye, key, value) => {
    setForm((prev) => ({
      ...prev,
      rx: {
        ...prev.rx,
        [eye]: {
          ...prev.rx[eye],
          [key]: value,
        },
      },
    }));
  };

  const toggleIndex = (indexValue) => {
    setForm((prev) => {
      const selected = new Set(prev.selectedIndices);
      if (selected.has(indexValue)) selected.delete(indexValue);
      else selected.add(indexValue);
      return {
        ...prev,
        selectedIndices: Array.from(selected).sort((a, b) => a - b),
        compareAllIndices: false,
      };
    });
  };
  const toggleDesign = (designValue) => {
  setForm((prev) => {
    const selected = new Set(prev.selectedDesigns ?? DEFAULT_DESIGNS);

    if (selected.has(designValue)) selected.delete(designValue);
    else selected.add(designValue);

    return {
      ...prev,
      selectedDesigns: Array.from(selected),
      compareAllIndices: false,
    };
  });
};

  const handleSave = async () => {
    if (!archive?.id) return;

    try {
      setSaveState('saving');
      await updateArchive(archive.id, {
        thicknessStudy: {
          input: form,
          result: study,
          updatedAt: Date.now(),
        },
      });
      setSaveState('saved');
      window.setTimeout(() => setSaveState('idle'), 1800);
    } catch (error) {
      console.error(error);
      setSaveState('error');
      window.setTimeout(() => setSaveState('idle'), 2200);
    }
  };

  return (
    <div className="thickness-page">
      <div className="thickness-shell glass-card">
        <div className="thickness-topbar">
          <button onClick={() => goTo('archives')}>⬅ Archives</button>
          <div className="thickness-client">
            <h1>Étude d&apos;épaisseur</h1>
            <p>{archive.clientName || 'Dossier sans nom'}</p>
          </div>
          <button onClick={handleSave} disabled={saveState === 'saving'}>
            {saveState === 'saving' ? 'Enregistrement...' : '💾 Enregistrer'}
          </button>
        </div>

        <div className="thickness-grid">
          <section className="thickness-panel">
            <h2>Données dossier</h2>
            <div className="data-pill-row">
              <span>DP OD : {archive.results?.DP_OD ?? '—'} mm</span>
              <span>DP OG : {archive.results?.DP_OG ?? '—'} mm</span>
              <span>H OD : {archive.results?.H_OD ?? '—'} mm</span>
              <span>H OG : {archive.results?.H_OG ?? '—'} mm</span>
            </div>

            <div className="form-grid two-columns">
              <label>
                Forme
                <select value={form.frameShape} onChange={(e) => handleChange('frameShape', e.target.value)}>
                  {FRAME_SHAPES.map((shape) => (
                    <option key={shape.id} value={shape.id}>{shape.label}</option>
                  ))}
                </select>
              </label>
              <label>
                Type de verre
                <select value={form.lensType} onChange={(e) => handleChange('lensType', e.target.value)}>
                  <option value="singleVision">Unifocal</option>
                  <option value="progressive">Progressif</option>
                </select>
              </label>
              <label>
                Largeur A (mm)
                <input type="number" value={form.A} onChange={(e) => handleChange('A', Number(e.target.value))} />
              </label>
              <label>
                Hauteur B (mm)
                <input type="number" value={form.B} onChange={(e) => handleChange('B', Number(e.target.value))} />
              </label>
              <label>
                Pont DBL (mm)
                <input type="number" value={form.DBL} onChange={(e) => handleChange('DBL', Number(e.target.value))} />
              </label>
              <label>
                Épaisseur mini (centre − / bord +) (mm)
                <input type="number" step="0.1" value={form.centerThickness} onChange={(e) => handleChange('centerThickness', Number(e.target.value))} />
              </label>
              <label>
                Hauteur OD (mm)
                <input type="number" step="0.1" value={form.fittingHeightRight} onChange={(e) => handleChange('fittingHeightRight', Number(e.target.value))} />
              </label>
              <label>
                Hauteur OG (mm)
                <input type="number" step="0.1" value={form.fittingHeightLeft} onChange={(e) => handleChange('fittingHeightLeft', Number(e.target.value))} />
              </label>
              <label>
                DP OD (mm)
                <input type="number" step="0.1" value={form.pdRight} onChange={(e) => handleChange('pdRight', Number(e.target.value))} />
              </label>
              <label>
                DP OG (mm)
                <input type="number" step="0.1" value={form.pdLeft} onChange={(e) => handleChange('pdLeft', Number(e.target.value))} />
              </label>
              {form.lensType === 'progressive' && (
                <label>
                  Addition
                  <input type="number" step="0.25" value={form.addition} onChange={(e) => handleChange('addition', Number(e.target.value))} />
                </label>
              )}
            </div>
          </section>

          <section className="thickness-panel">
            <h2>Rx</h2>
            <div className="rx-grid">
              {['od', 'og'].map((eye) => (
                <div key={eye} className="rx-card">
                  <h3>{eye.toUpperCase()}</h3>
                  <label>
                    Sphère
                    <input
  type="text"
  inputMode="text"
  value={form.rx[eye].sphere}
  onChange={(e) => handleRxChange(eye, 'sphere', e.target.value)}
/>
                  </label>
                  <label>
                    Cylindre
                    <input
  type="text"
  inputMode="text"
  value={form.rx[eye].cylinder}
  onChange={(e) => handleRxChange(eye, 'cylinder', e.target.value)}
/>
                  </label>
                  <label>
                    Axe
                    <input type="number" step="1" min="0" max="180" value={form.rx[eye].axis} onChange={(e) => handleRxChange(eye, 'axis', Number(e.target.value))} />
                  </label>
                </div>
              ))}
            </div>

            <div className="index-box">
  <label className="checkbox-line">
    <input
      type="checkbox"
      checked={form.compareAllIndices}
      onChange={(e) => handleChange('compareAllIndices', e.target.checked)}
    />
    Comparer automatiquement tous les indices
  </label>

  {!form.compareAllIndices && (
    <>
      <div className="index-chip-row">
        {DEFAULT_INDICES.map((indexValue) => (
          <button
            key={indexValue}
            type="button"
            className={`index-chip ${(form.selectedIndices ?? []).includes(indexValue) ? 'active' : ''}`}
            onClick={() => toggleIndex(indexValue)}
          >
            {indexValue.toFixed(2)}
          </button>
        ))}
      </div>

      <div className="index-chip-row" style={{ marginTop: '10px' }}>
        {DEFAULT_DESIGNS.map((designValue) => (
          <button
            key={designValue}
            type="button"
            className={`index-chip ${(form.selectedDesigns ?? []).includes(designValue) ? 'active' : ''}`}
            onClick={() => toggleDesign(designValue)}
          >
            {designValue}
          </button>
        ))}
      </div>
    </>
  )}
</div>
          </section>
        </div>

        <div className="preview-grid">
  <section className="thickness-panel">
    <div className="view-toggle-row">
  <h2>{viewMode === '2d' ? 'Vue monture' : 'Vue 3D'}</h2>

  <div className="view-toggle-controls">
    {viewMode === '3d' && available3DMaterials.length > 0 && (
      <label className="material-select-inline">
        <span>Matériau affiché</span>
        <select
          value={selected3DMaterial}
          onChange={(e) => setSelected3DMaterial(e.target.value)}
        >
          {available3DMaterials.map((label) => (
            <option key={label} value={label}>
              {label}
            </option>
          ))}
        </select>
      </label>
    )}

    <div className="view-toggle-buttons">
      <button
        type="button"
        className={viewMode === '2d' ? 'active' : ''}
        onClick={() => setViewMode('2d')}
      >
        2D
      </button>
      <button
        type="button"
        className={viewMode === '3d' ? 'active' : ''}
        onClick={() => setViewMode('3d')}
      >
        3D
      </button>
    </div>
  </div>
</div>

    <div className="svg-grid">
      {viewMode === '2d' ? (
        <>
          <LensPreview
            title="OD"
            eyeResult={study.right}
            A={study.meta.A}
            B={study.meta.B}
          />
          <LensPreview
            title="OG"
            eyeResult={study.left}
            A={study.meta.A}
            B={study.meta.B}
          />
        </>
      ) : (
        <>
          <Lens3DPreview
  title="OD"
  eyeResult={study.right}
  selectedMaterialLabel={selected3DMaterial}
/>

<Lens3DPreview
  title="OG"
  eyeResult={study.left}
  selectedMaterialLabel={selected3DMaterial}
/>
        </>
      )}
    </div>
  </section>

  <section className="thickness-panel">
    <h2>Résumé</h2>
    <div className="result-summaries">
      <SummaryCard label="OD" eyeResult={study.right} />
      <SummaryCard label="OG" eyeResult={study.left} />
    </div>
  </section>
</div>

<section className="thickness-panel full-width-table">
  <h2>Comparatif d'épaisseur bord / centre</h2>
  <ResultsTable study={study} />
</section>
      </div>
    </div>
  );
}

function SummaryCard({ label, eyeResult }) {
  const comparisons = eyeResult?.comparisons ?? [];

  const getMainThickness = (item) =>
    item?.thicknessType === 'center' ? item?.centerThickness : item?.edgeThickness;

  if (!comparisons.length) {
    return (
      <div className="summary-card">
        <h3>{label}</h3>
        <p>Aucune donnée disponible</p>
      </div>
    );
  }

  const best = comparisons.reduce((acc, item) =>
    getMainThickness(item) < getMainThickness(acc) ? item : acc,
    comparisons[0]
  );

  const displayedThickness =
    best?.thicknessType === 'center' ? best?.centerThickness : best?.edgeThickness;

  return (
    <div className="summary-card">
      <h3>{label}</h3>
      <p>Puissance retenue : {eyeResult.effectivePower.toFixed(2)} D</p>
      <p>Diamètre utile : {eyeResult.usefulDiameter.toFixed(1)} mm</p>
      <p>Zone max probable : {eyeResult.maxPoint.zone}</p>
      <p>Type d’épaisseur : {best?.thicknessType === 'center' ? 'centre' : 'bord'}</p>
      <p>Bord : {best?.edgeThickness?.toFixed(1)} mm</p>
      <p>Centre : {best?.centerThickness?.toFixed(1)} mm</p>
      <p>Meilleur indice sélectionné : {best?.label ?? best?.index?.toFixed(2)} → {displayedThickness?.toFixed(1)} mm</p>
    </div>
  );
}

function ResultsTable({ study }) {
  const rows = study.right.comparisons.map((od, index) => {
    const og = study.left.comparisons[index];
    return { od, og };
  });

  return (
  <div className="table-wrap">
    <table className="thickness-table">
      <thead>
        <tr>
          <th>Indice</th>
          <th>OD bord</th>
          <th>OD centre</th>
          <th>Gain OD</th>
          <th>OG bord</th>
          <th>OG centre</th>
          <th>Gain OG</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(({ od, og }) => (
          <tr key={od.label}>
            <td>{od.label}</td>

            <td>
              {od?.thicknessType === 'center'
                ? `max ${od?.maxEdgeThickness?.toFixed(1)} mm`
                : `${od?.edgeThickness?.toFixed(1)} mm`}
            </td>

            <td>
              {od?.thicknessType === 'center'
                ? `${od?.centerThickness?.toFixed(1)} mm`
                : ''}
            </td>

            <td>{formatGain(od?.cosmeticGainVs15)}</td>

            <td>
              {og?.thicknessType === 'center'
                ? `max ${og?.maxEdgeThickness?.toFixed(1)} mm`
                : `${og?.edgeThickness?.toFixed(1)} mm`}
            </td>

            <td>
              {og?.thicknessType === 'center'
                ? `${og?.centerThickness?.toFixed(1)} mm`
                : ''}
            </td>

            <td>{formatGain(og?.cosmeticGainVs15)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);
}
function getDisplayedFittingPoint(fittingPoint) {
  if (!fittingPoint) return { x: 0, y: 0 };

  return {
    x: -fittingPoint.x,
    y: fittingPoint.y,
  };
}
function getDisplayedZone(zone, title) {
  if (!zone) return '';

  if (title === 'OD') {
    return zone;
  }

  return zone
    .replace('nasal', '__TMP_NASAL__')
    .replace('temporal', 'nasal')
    .replace('__TMP_NASAL__', 'temporal');
}
function formatCO(fittingPoint, eye) {
  if (!fittingPoint) return '';

  const x = -fittingPoint.x;
  const y = fittingPoint.y;

  let horizontal = '';
  let vertical = '';

  if (eye === 'OD') {
    horizontal = x > 0
      ? `${Math.abs(x).toFixed(1)} mm nasal`
      : `${Math.abs(x).toFixed(1)} mm temporal`;
  } else {
    horizontal = x > 0
      ? `${Math.abs(x).toFixed(1)} mm temporal`
      : `${Math.abs(x).toFixed(1)} mm nasal`;
  }

  if (y > 0) vertical = `${Math.abs(y).toFixed(1)} mm haut`;
  else if (y < 0) vertical = `${Math.abs(y).toFixed(1)} mm bas`;
  else vertical = `0 mm`;

  return `${horizontal} / ${vertical}`;
}
function getThicknessColor(thickness, min, max) {
  if (max <= min) return 'rgb(120, 180, 255)';

  const t = (thickness - min) / (max - min);

  const r = Math.round(255 * t);
  const g = Math.round(160 * (1 - t));
  const b = Math.round(255 * (1 - t));

  return `rgb(${r}, ${g}, ${b})`;
}

function buildHeatSegments(thicknessMap, mirrorX, width, height, scale) {
  if (!Array.isArray(thicknessMap) || thicknessMap.length < 2) return [];

  const thicknessValues = thicknessMap.map((p) => p.thickness);
  const minThickness = Math.min(...thicknessValues);
  const maxThickness = Math.max(...thicknessValues);

  return thicknessMap.map((point, index) => {
    const next = thicknessMap[(index + 1) % thicknessMap.length];

    return {
      x1: width / 2 + mirrorX(point.x) * scale,
      y1: height / 2 - point.y * scale,
      x2: width / 2 + mirrorX(next.x) * scale,
      y2: height / 2 - next.y * scale,
      color: getThicknessColor(point.thickness, minThickness, maxThickness),
    };
  });
}

function LensPreview({ title, eyeResult, A, B }) {
  const mirrorX = (x) => -x;

  const width = 260;
  const height = 220;
  const margin = 18;
  const scaleX = (width - margin * 2) / A;
  const scaleY = (height - margin * 2) / B;
  const scale = Math.min(scaleX, scaleY);

  const isPositive = eyeResult.effectivePower > 0;

  const points = eyeResult.shapePoints
    .map((point) => `${width / 2 + mirrorX(point.x) * scale},${height / 2 - point.y * scale}`)
    .join(' ');

  const heatSegments = buildHeatSegments(
    eyeResult.thicknessMap,
    mirrorX,
    width,
    height,
    scale
  );

  const fitX = width / 2 + mirrorX(eyeResult.fittingPoint.x) * scale;
  const fitY = height / 2 - eyeResult.fittingPoint.y * scale;
  const maxX = width / 2 + mirrorX(eyeResult.maxPoint.x) * scale;
  const maxY = height / 2 - eyeResult.maxPoint.y * scale;

  return (
    <div className="lens-preview-card">
      <h3>{title}</h3>

      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="lens-preview-svg"
        role="img"
        aria-label={`Prévisualisation ${title}`}
      >
        {!isPositive &&
  heatSegments.map((segment, index) => (
    <line
      key={index}
      x1={segment.x1}
      y1={segment.y1}
      x2={segment.x2}
      y2={segment.y2}
      stroke={segment.color}
      strokeWidth="3"
      strokeLinecap="round"
    />
  ))}

<defs>
  <radialGradient id={`centerThickness-${title}`}>
    <stop offset="0%" stopColor="rgba(255,80,80,0.55)" />
    <stop offset="70%" stopColor="rgba(255,80,80,0.20)" />
    <stop offset="100%" stopColor="rgba(255,80,80,0)" />
  </radialGradient>
</defs>

{isPositive && (
  <circle
    cx={width / 2}
    cy={height / 2}
    r="55"
    fill={`url(#centerThickness-${title})`}
  />
)}

<polygon points={points} fill="none" className="frame-outline" />

        <line
          x1={width / 2}
          y1={10}
          x2={width / 2}
          y2={height - 10}
          className="helper-line"
        />
        <line
          x1={10}
          y1={height / 2}
          x2={width - 10}
          y2={height / 2}
          className="helper-line"
        />
        <circle cx={width / 2} cy={height / 2} r="4" className="center-point" />
        <circle cx={fitX} cy={fitY} r="5" className="fit-point" />
        <circle cx={maxX} cy={maxY} r="5" className="max-point" />
        <line x1={fitX} y1={fitY} x2={maxX} y2={maxY} className="radius-line" />
      </svg>

      <div className="legend-grid">
        <span>CO : {formatCO(eyeResult.fittingPoint, title)}</span>
        <span>Zone max: {getDisplayedZone(eyeResult.maxPoint.zone, title)}</span>
        <span>{isPositive ? 'Rouge = épaisseur au centre' : 'Rouge = épaisseur au bord'}</span>
      </div>
    </div>
  );
}

function formatGain(value) {
  if (typeof value !== 'number') return '—';
  return value > 0 ? `-${value.toFixed(1)} mm` : '0.0 mm';
}
